import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes } from '@fortawesome/free-solid-svg-icons';
import useFormHook from '@shared/hooks/useFormHook';
import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import CustomDatePicker from '@shared/components/CustomDatePicker';
import LoadingButton from '@shared/components/LoadingButton';
import { companiesService } from '@shared/services/companiesService';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { useForm } from 'react-hook-form';

function normalizeCompany(company, fallbackId) {
    if (!company) {
        return null;
    }

    const id = company.id ?? fallbackId;
    const code = company.code || company.branchNo || company.branchCode || '';
    const name = company.name || company.branchName || company.longDescription || '';

    return {
        ...company,
        id,
        code,
        name,
    };
}

function getCompanyLabel(company) {
    if (!company) {
        return '';
    }

    const code = company.code || company.branchNo || '';
    const name = company.name || company.branchName || '';

    if (code && name) {
        return `${code} - ${name}`;
    }

    return name || code || String(company.id ?? '');
}

function resolveEditCompanyId(editData, companies, defaultCompany, adminBranchId) {
    let companyId = editData.empBranchId ?? editData.empBranches?.id;

    if (!companyId && editData.companyName && companies.length > 0) {
        const searchName = editData.companyName.toLowerCase();
        const foundCompany = companies.find((company) => {
            const candidates = [
                company.name,
                company.branchName,
                getCompanyLabel(company),
            ]
                .filter(Boolean)
                .map((value) => value.toLowerCase());

            return candidates.some((value) => value === searchName || value.includes(searchName));
        });
        companyId = foundCompany?.id;
    }

    if (!companyId && editData.empBranches?.name && companies.length > 0) {
        const searchName = editData.empBranches.name.toLowerCase();
        const foundCompany = companies.find((company) => {
            const name = (company.name || company.branchName || '').toLowerCase();
            return name === searchName;
        });
        companyId = foundCompany?.id;
    }

    if (!companyId) {
        companyId = defaultCompany?.id ?? companies[0]?.id ?? adminBranchId;
    }

    return companyId != null ? String(companyId) : '';
}

export default function LevelOfStructuralForm({ onClose, onConfirm, editData, defaultCompany = null }) {
    const { t } = useTranslation();
    const { modalRef } = useFormHook({}, onClose);
    const adminBranchId = useSelector((state) => state.auth.adminBranchId);

    // Helper function to parse dd.MM.yyyy format
    // Sets time to noon (12:00:00) to avoid timezone issues
    const parseDDMMYYYY = (dateString) => {
        if (!dateString) return null;
        
        // If it's already a Date object, set time to noon
        if (dateString instanceof Date) {
            const date = new Date(dateString);
            date.setHours(12, 0, 0, 0);
            return date;
        }
        
        // Try to parse dd.MM.yyyy format
        const parts = dateString.split('.');
        if (parts.length === 3) {
            const [day, month, year] = parts;
            const date = new Date(year, month - 1, day, 12, 0, 0, 0);
            if (!isNaN(date.getTime())) {
                return date;
            }
        }
        
        // Fallback: try standard Date parsing, set to noon
        const date = new Date(dateString);
        if (!isNaN(date.getTime())) {
            date.setHours(12, 0, 0, 0);
            return date;
        }
        
        console.error(`❌ [parseDDMMYYYY] Failed to parse:`, dateString);
        return null;
    };

    const validationSchema = yup.object().shape({
        company: yup.string().required(t('common.required')),
        level: yup.string().required(t('common.required')),
        abbreviation: yup.string().required(t('common.required')),
        title: yup.string().required(t('common.required')),
        fromDate: yup.date().required(t('common.required')),
        toDate: yup.date().required(t('common.required')),
    });
    // Create default date set to noon to avoid timezone issues
    const getDefaultDate = () => {
        const date = new Date();
        date.setHours(12, 0, 0, 0);
        return date;
    };

    const { register, handleSubmit, setValue, watch, formState: { errors }, reset, trigger } = useForm({
        resolver: yupResolver(validationSchema),
        mode: 'onChange',
        reValidateMode: 'onChange',
        criteriaMode: 'all',
        defaultValues: {
            company: '',
            level: '',
            code: '',
            abbreviation: '',
            title: '',
            fromDate: getDefaultDate(),
            toDate: '2099-12-31',
            indefinite: true
        }
    });
    const [isIndefinite, setIsIndefinite] = useState(true);
    const [companies, setCompanies] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);



    // Watch the indefinite checkbox value
    const indefiniteValue = watch('indefinite');

    // Update isIndefinite state when checkbox changes
    useEffect(() => {
        setIsIndefinite(indefiniteValue);
        if (indefiniteValue) {
            setValue('toDate', '2099-12-31');
            trigger('toDate');
        }
    }, [indefiniteValue, setValue, trigger]);


    // Fetch admin branch company only
    useEffect(() => {
        const applyCompany = (company) => {
            const normalizedCompany = normalizeCompany(company, adminBranchId);
            if (!normalizedCompany) {
                setCompanies([]);
                return;
            }

            setCompanies([normalizedCompany]);

            if (!editData) {
                setValue('company', String(normalizedCompany.id));
            }
        };

        const fetchCompany = async () => {
            if (adminBranchId === null || adminBranchId === undefined) {
                setCompanies([]);
                setLoading(false);
                return;
            }

            if (defaultCompany) {
                applyCompany(defaultCompany);
                setLoading(false);
                return;
            }

            try {
                setLoading(true);
                setError(null);
                const company = await companiesService.getSelectedCompany(adminBranchId);
                applyCompany(company);
            } catch (err) {
                console.error('Error fetching company:', err);
                setError('Failed to load companies');
                setCompanies([]);
            } finally {
                setLoading(false);
            }
        };

        fetchCompany();
    }, [adminBranchId, defaultCompany, editData, setValue]);

    // Reset defaults when opening add modal (company is set by fetch/defaultCompany)
    useEffect(() => {
        if (editData) {
            return;
        }

        reset({
            level: '',
            code: '',
            abbreviation: '',
            title: '',
            fromDate: getDefaultDate(),
            toDate: '2099-12-31',
            indefinite: true,
        });
        setIsIndefinite(true);
    }, [editData, reset]);

    // Set initial values if editing
    useEffect(() => {
        if (!editData || companies.length === 0) {
            return;
        }

        let companyId = resolveEditCompanyId(editData, companies, defaultCompany, adminBranchId);

        setValue('company', companyId);
        setValue('level', editData.planLevel);
        setValue('code', editData.code);
        setValue('abbreviation', editData.shortDescription);
        setValue('title', editData.longDescription);

        const parsedFromDate = parseDDMMYYYY(editData.fromDate);
        const parsedToDate = parseDDMMYYYY(editData.toDate);
        const isIndefiniteDate = editData.toDate === '31.12.2099' || editData.toDate === '2099-12-31' || editData.indefinite;

        setValue('fromDate', parsedFromDate || new Date());
        setValue('toDate', isIndefiniteDate ? '2099-12-31' : parsedToDate);
        setValue('indefinite', isIndefiniteDate);
        setIsIndefinite(isIndefiniteDate);
    }, [editData, companies, setValue, defaultCompany, adminBranchId]);

    const submitForm = async (data) => {
        try {
            setLoading(true);
            
            // Format dates to ensure correct day is sent
            const formatDateForSubmit = (date) => {
                if (!date) return null;
                // If it's the indefinite date string, parse it correctly
                if (date === '2099-12-31') {
                    const d = new Date(2099, 11, 31, 12, 0, 0, 0);
                    return d;
                }
                const d = new Date(date);
                // Ensure time is set to noon to avoid timezone shifts
                d.setHours(12, 0, 0, 0);
                return d;
            };
            
            const formData = {
                company: data.company,
                level: data.level,
                code: data.code,
                abbreviation: data.abbreviation,
                title: data.title,
                fromDate: formatDateForSubmit(data.fromDate),
                toDate: data.indefinite ? formatDateForSubmit('2099-12-31') : formatDateForSubmit(data.toDate)
            };
            
            if (onConfirm) {
                await onConfirm(formData);
            }
        } catch (error) {
            console.error('Error saving structural level:', error);
        } finally {
            setLoading(false);
        }
    };

    // Debug: Log watched values
    const watchedFromDate = watch('fromDate');
    const watchedToDate = watch('toDate');

    return (
        <div className="modal show" tabIndex="-1">
            <div className="modal-dialog modal-confirm" ref={modalRef}>
                <div className="modal-content">
                    <button type="button" className="close" onClick={onClose}>
                        <FontAwesomeIcon icon={faTimes} />
                    </button>
                    <div className="modal-body">
                        <h4 className="modal-title">
                            {editData ? t('common.edit') : t('common.add')} {t('organizationalStructure.levelsOfStructuralGroups.title')}
                        </h4>
                        <form id="structuralLevelForm" className="region-form" onSubmit={handleSubmit(submitForm)}>
                            <div className="form-sections">
                                <div className="form-section">
                                    <div className="form-group">
                                        <span className="span-bold">{t('organizationalStructure.levelsOfStructuralGroups.form.company')} <span className="required">*</span></span>
                                        <select
                                            className="form-control"
                                            {...register('company', {
                                                required: true,
                                                onChange: () => trigger('company')
                                            })}
                                            disabled={loading || !!editData || companies.length <= 1}
                                        >
                                            {companies.length === 0 && (
                                                <option value="">{t('common.select')}</option>
                                            )}
                                            {companies.map(company => (
                                                <option key={company.id} value={String(company.id)}>
                                                    {getCompanyLabel(company)}
                                                </option>
                                            ))}
                                        </select>
                                        {error && <p className="text-danger">{error}</p>}
                                        {errors.company && <p className="text-danger">{errors.company.message}</p>}
                                    </div>

                                    <div className="form-group">
                                        <span className="span-bold">{t('organizationalStructure.levelsOfStructuralGroups.form.level')} <span className="required">*</span></span>
                                        <input
                                            maxLength={2}
                                            type="text"
                                            className="form-control"
                                            {...register('level', { required: true })}
                                        />
                                        {errors.level && <p className="text-danger">{errors.level.message}</p>}
                                    </div>

                                    <div className="form-group">
                                        <span className="span-bold">{t('organizationalStructure.levelsOfStructuralGroups.form.code')}</span>
                                        <input
                                            maxLength={10}
                                            type="text"
                                            className="form-control"
                                            {...register('code', { required: true })}
                                        />
                                    </div>

                                    <div className="form-group">
                                        <span className="span-bold">{t('organizationalStructure.levelsOfStructuralGroups.form.abbreviation')} <span className="required">*</span></span>
                                        <input
                                            maxLength={20}
                                            type="text"
                                            className="form-control"
                                            {...register('abbreviation', { required: true })}
                                        />
                                        {errors.abbreviation && <p className="text-danger">{errors.abbreviation.message}</p>}
                                    </div>
                                </div>

                                <div className="form-section">
                                    <div className="form-group">
                                        <span className="span-bold">{t('organizationalStructure.levelsOfStructuralGroups.form.title')} <span className="required">*</span></span>
                                        <input
                                            maxLength={80}
                                            type="text"
                                            className="form-control"
                                            {...register('title', { required: true })}
                                        />
                                        {errors.title && <p className="text-danger">{errors.title.message}</p>}
                                    </div>

                                    <div className="form-group">
                                        <span className="span-bold">{t('organizationalStructure.levelsOfStructuralGroups.form.validity')}</span>
                                        <div className="checkbox-wrapper-start title-checkbox">
                                            <input
                                                type="checkbox"
                                                id="indefinite"
                                                {...register('indefinite')}
                                            />
                                            <label htmlFor="indefinite">{t('organizationalStructure.levelsOfStructuralGroups.form.indefinite')}</label>
                                        </div>
                                    </div>

                                    <div className="form-group">
                                        <span className="span-bold">{t('organizationalStructure.levelsOfStructuralGroups.form.fromDate')} <span className="required">*</span></span>
                                        <CustomDatePicker
                                            name="fromDate"
                                            value={watchedFromDate}
                                            onChange={(date) => {
                                                setValue('fromDate', date);
                                                trigger('fromDate');
                                            }}
                                            placeholderText={t('common.dateFormat')}
                                        />
                                        {errors.fromDate && <p className="text-danger">{errors.fromDate.message}</p>}
                                    </div>

                                    <div className="form-group">
                                        <span className="span-bold">{t('organizationalStructure.levelsOfStructuralGroups.form.toDate')} <span className="required">*</span></span>
                                        <CustomDatePicker
                                            name="toDate"
                                            value={watchedToDate === '2099-12-31' ? '' : watchedToDate}
                                            onChange={(date) => {
                                                setValue('toDate', date);
                                                // If user selects a date, uncheck indefinite
                                                if (date && isIndefinite) {
                                                    setValue('indefinite', false);
                                                    setIsIndefinite(false);
                                                }
                                                trigger('toDate');
                                            }}
                                            placeholderText={t('common.dateFormat')}
                                            disabled={isIndefinite}
                                        />
                                        {isIndefinite && (
                                            <div className="indefinite-notice">
                                                <span className="text-muted">{t('common.indefinite')}</span>
                                            </div>
                                        )}
                                        {errors.toDate && <p className="text-danger">{errors.toDate.message}</p>}
                                    </div>
                                </div>
                            </div>
                        </form>
                    </div>
                    <div className="modal-footer">
                        <LoadingButton type="submit" form="structuralLevelForm" className="btn btn-primary" loading={loading} disabled={loading}>
                            {t('common.save')}
                        </LoadingButton>
                        <button type="button" className="btn btn-secondary" onClick={onClose} disabled={loading}>
                            {t('common.cancel')}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}
